<template>
  <v-app>

    <div class="application flex flex-column justify-center items-center">

      <form
        @submit.prevent="submit"
        class="flex flex-none flex-column items-center p1"
        style="width: 100%; max-width: 500px;"
        >
        <v-card
          class="flex flex-column flex-none items-center bg-white elevation-15"
          :class="$mq.xs ? 'p3' : 'p4'"
          style="width: 100%;"
          >

          <LogoEnterpriseHqStack style="margin: 24px 32px 48px;" />

          <v-dialog
            v-model="showQRcode"
            width="660px"
          > 
            <v-card>
              <v-card-title>
                <h2>Multi-Factor Authorization</h2>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text >
                <div>Your system administrator has enabled multi-factor authorization for your LENS account.</div>
                <div>In order to log in to your LENS account, you will need to do the following:</div>
                <p></p>
                <div style='margin-left:15px;'>
                  <ul>
                    <li>Install Google Authenticator onto your smart phone or other device.</li>
                    <li>Enter the following code into Google Authenticator. You can do that by showing Authenticator the following QR code, or by typing the issuer, Authorizer Name and Google Secret below into Authenticator.</li>
                  </ul>
                </div>
                <div>
                  <img class="qr-code" :src="google_mfa.google_qr_uri" />
                </div>
                <div style='margin-left:15px;'>
                  <table>
                    <tr><td><strong>Issuer:</strong></td><td>{{ google_mfa.google_mfa_issuer }}</td></tr>
                    <tr><td><strong>Authorizer Name:</strong></td><td>{{ google_mfa.google_auth_name }}</td></tr>
                    <tr><td><strong>Google Secret:</strong></td><td>{{ google_mfa.google_mfa_secret }}</td></tr>
                  </table>
                  <br />
                  <ul>
                    <li>After entering the code into Google Authenticator you should be ready to login with multi-factor authorization.</li>
                    <li>Click OK.</li>
                    <li>Enter your email/password as usual.</li>
                    <li>You will be prompted to enter a multi-factor code.</li>
                    <li>On your device, request a new code, and enter it where prompted.</li>
                    <li>You should be successfully logged in.</li>
                    <li><strong>Note: <span style="color:red;">After clicking "OK", you will not be shown this QR code and Google Secret again.</span></strong></li>
                  </ul>
                </div>
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <v-btn
                  color="green" tile
                  @click="clickQRCodeConfirm()"
                >OK</v-btn>
                <v-spacer />
                <v-btn
                  color="grey" tile
                  @click="clickQRCodeCancel()"
                >Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <transition name="inOut">
            <div v-if="!settingPassword" style="width: 100%">
              <v-text-field
                label="Email"
                v-model.trim="email"
                type="email"
                ref="email"
              />
            </div>
          </transition>

          <transition name="inOut">
            <div v-if="!resettingPassword" style="width: 100%">
              <p color="red" v-if="expiredPassword">Your password has expired. You must set a new password to continue. Passwords expire after 90 days.</p>
              <v-text-field
                :label="settingPassword ? 'Set Your New Password' : 'Password'"
                v-model.trim="password"
                :append-icon="passwordVisible ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="passwordVisible = !passwordVisible"
                :type="passwordVisible ? 'text' : 'password'"
                ref="password"
                :hint="settingPassword ? 'Must have at least 10 characters, including at least 1 lowercase, 1 uppercase, 1 special character and 1 digit' : ''"
              />
            </div>
          </transition>

          <transition name="inOut">
            <div v-if="!resettingPassword && mfaCodeRequired && !expiredPassword" style="width: 100%">
              <v-text-field
                label="Two-Factor Authorization Code"
                v-model.trim="mfa_code"
                type="text"
                ref="text"
              />
            </div>
          </transition>

          <transition name="inOut">
            <p class="center error-text" v-if="errorMessage" >
              {{errorMessage}}
            </p>
          </transition>

          <div style="width: 100%">
            <v-btn
              type="submit"
              :color="success ? 'success' : 'primary'"
              block
              outlined
              :disabled="$v.$invalid"
              >
              {{submitButtonText}}
            </v-btn>
          </div>
        </v-card>
        <div class="mt2">
          <v-btn
            text
            color="white"
            class="m0"
            :to="{path: resettingPassword || settingPassword ? '/login' : '/reset-password', query: $route.query}"
            >
            <span key="1" v-if="resettingPassword || settingPassword">Log in</span>
            <span key="2" v-else>Reset my password</span>
          </v-btn>
        </div>
      </form>

    </div>
  </v-app>
</template>


<script>
/* @flow */
import delay from 'delay'
import {validationMixin} from 'vuelidate'
// $FlowFixMe
import {required, minLength} from 'vuelidate/lib/validators'
import LogoEnterpriseHqStack from '@/components/LogoEnterpriseHqStack'

import {CREATE_SESSION, NEW_SESSION_FROM_META_OBJECT} from '@/store/session'
import {USER_FORGOT_PASSWORD, USER_RESET_PASSWORD, VERIFY_NEW_USER} from '@/store/users'

const loginButtonText = 'Log in'
const resetPasswordButtonText = 'Reset My Password'
const setPasswordButtonText = 'Set Password'

export default {
  name: 'Login',
  mixins: [validationMixin],
  validations() {
    if (this.resettingPassword) {
      return {email: {required}}
    } else if (this.settingPassword) {
      return {password: {required, minLength: minLength(8)}}
    } else {
      return {email: {required}, password: {required}}
    }
  },
  data() {
    return {
      email: '',
      password: '',
      passwordVisible: false,
      success: false,
      submitButtonText: '',
      errorMessage: '',
      accountLocked: false,
      mfaCodeRequired: false,
      google_mfa: {},
      showQRcode: false,
      mfa_code: null
    }
  },
  computed: {
    resettingPassword() {
      return this.$route.path === '/reset-password'
    },
    settingPassword() {
      return this.$route.path === '/set-password'
    },
    expiredPassword() {
      return !!this.$route.query.expired_password
    },
  },
  mounted() {
    this.setInitialButtonText()
    this.setInitialFocus()
  },
  watch: {
    async '$route.path'() {
      this.setInitialButtonText()
      this.success = false
      this.errorMessage = ''
      await this.$nextTick()
      this.setInitialFocus()
    },
  },
  methods: {
    clickQRCodeConfirm(){
      this.errorMessage = ''
      this.submitButtonText = 'LOG IN'
      this.mfaCodeRequired = true
      this.showQRcode = false
    },
    clickQRCodeCancel(){
      this.errorMessage = ''
      this.submitButtonText = 'LOG IN'
      this.mfaCodeRequired = false
      this.showQRcode = false
    },
    setInitialFocus() {
      if (this.resettingPassword) {
        this.$refs.email && this.$refs.email.focus()
      } else if (this.settingPassword) {
        this.submitButtonText = setPasswordButtonText
        this.$refs.password && this.$refs.password.focus()
      } else {
        this.$refs.email && this.$refs.email.focus()
      }
    },
    setInitialButtonText() {
      if (this.resettingPassword) {
        this.submitButtonText = resetPasswordButtonText
      } else if (this.settingPassword) {
        this.submitButtonText = setPasswordButtonText
      } else {
        this.submitButtonText = loginButtonText
      }
    },
    async handleSetPassword(){
      this.errorMessage = ''
      this.submitButtonText = 'Setting password...'
      const {success, error, meta} = await this.$store.dispatch(USER_RESET_PASSWORD, {
        reset_password_token: this.$route.query.reset_password_token,
        new_password: this.password,
      })

      if (success) {
        this.success = true
        this.submitButtonText = 'Success! Logging in...'
        await this.$store.dispatch(NEW_SESSION_FROM_META_OBJECT, meta)
        this.loginFlow()
      } else {
        this.errorMessage = error
        this.setInitialButtonText()
      }
    },
    async loginFlow() {
      // Wait some time to user sees the 'Success' login message
      await delay(200)

      let redirect = this.$route.query.redirect
      if (redirect && redirect.includes('redirect')) {
        // Sometimes the redirect query string will get filled with nested redirect queries. Not sure why,
        // but when it happens, it breaks the login button. This is a workaround
        redirect = null
      }
      this.$router.push(redirect || '/dashboard')
    },
    async submit() {
      if (this.$v.$invalid) return

      if (this.resettingPassword) {
        this.errorMessage = ''
        this.submitButtonText = 'Sending reset email...'
        const {success, error} = await this.$store.dispatch(USER_FORGOT_PASSWORD, {
          email: this.email,
        })
        if (success) {
          this.success = true
          this.submitButtonText = 'Success! Check your email'
        } else {
          this.errorMessage = error
          this.setInitialButtonText()
        }
      } else if (this.settingPassword && this.$route.query.reset_password_token) {
        this.handleSetPassword()
      } else if (this.settingPassword && this.$route.query.new_user_token) {
        this.errorMessage = ''
        this.submitButtonText = 'Setting password...'
        const {success, error, meta} = await this.$store.dispatch(VERIFY_NEW_USER, {
          new_user_token: this.$route.query.new_user_token,
          new_password: this.password,
        })
        if (success) {
          this.success = true
          this.submitButtonText = 'Success! Logging in...'
          await this.$store.dispatch(NEW_SESSION_FROM_META_OBJECT, meta)
          this.loginFlow()
        } else {
          this.errorMessage = error
          this.setInitialButtonText()
        }
      } else {
        this.errorMessage = ''
        this.submitButtonText = 'Authenticating...'
        const {success, error, meta, reset_password_token } = 
          await this.$store.dispatch(CREATE_SESSION, {
            email: this.email,
            password: this.password,
            mfa_code: this.mfa_code,
          })
        console.log(reset_password_token)
        let google_mfa = meta && meta.google_mfa
        if (google_mfa && google_mfa.google_mfa_secret) {
          if (google_mfa.acknowledge_secret_please) {

          }  
          // show a popup noting mfa and showing qr code
          this.google_mfa = google_mfa
          this.showQRcode = true
        } else if (google_mfa && google_mfa.mfa_code_required) {
          this.submitButtonText = 'Verify Code'
          this.mfaCodeRequired=true
        } else if (success) {
          this.success = true
          this.submitButtonText = 'Success!'
          this.loginFlow()
        } else if(reset_password_token) {
          this.mfa_code=null
          this.mfaCodeRequired=false
          this.$router.push("/set-password?expired_password=1&reset_password_token=" + reset_password_token )
        } else {
          this.errorMessage = error
          this.setInitialButtonText()
        }
      }
    },
  },
  components: {
    LogoEnterpriseHqStack,
  },
}
</script>


<style scoped>
.application {
  transition: all 0.5s ease;
  background: #6c7884 !important;
}
.application:hover {
  background: #0e4a81 !important;
}
.btn,
.btn:first-child {
  /* Remove transition because of bad performance */
  transition: unset;
}

img {
  max-height: 67px;
}

img.qr-code {
  max-height: 200px;
  height: 200px;
  width: 200px;
}

.card {
  transition: max-height 0.3s ease-in-out;
}

button {
  /* Fix height issue in Firefox */
  flex: initial;
  /* Fix IE display issue */
  flex-basis: 36px;
}

.input-group {
  /* Fix IE display issue */
  flex-basis: 74px;
}

.inOut-enter-active,
.inOut-leave-active {
  transition: all 0.3s ease-in-out;
  max-height: 80px;
}
.inOut-enter,
.inOut-leave-to {
  opacity: 0;
  transform: scale(0);
  max-height: 0px;
}
</style>



// WEBPACK FOOTER //
// src/screens/Login.vue